import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";

// ─── SENTRY ────────────────────────────────────────────────────────────────
// Init before the first React render so the ErrorBoundary and any early
// throw (render error, chunk-load failure, etc.) are captured. No-op when
// REACT_APP_SENTRY_DSN is unset (local dev without Sentry).
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "preview",
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay is off by default — flip sampleRate to 0.1 later if you want
      // session recordings. Storage-heavy on free tier.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // 10% of page transactions traced — plenty for perf insight.
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0, // capture a replay whenever an error fires
    // Scrub Authorization / Cookie headers + password fields before send.
    beforeSend(event) {
      if (event.request?.headers) {
        for (const h of ["Authorization", "Cookie", "X-Api-Key", "X-Postback-Key"]) {
          delete event.request.headers[h];
        }
      }
      if (event.request?.data && typeof event.request.data === "object") {
        for (const k of ["password", "new_password", "old_password", "api_key", "token"]) {
          if (k in event.request.data) event.request.data[k] = "[scrubbed]";
        }
      }
      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
// Sentry.ErrorBoundary wraps the whole app so any uncaught render/hook
// exception is auto-sent with component stack + breadcrumbs. The fallback
// is intentionally plain — we don't want to mask real errors during dev.
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 32, fontFamily: "system-ui", textAlign: "center" }}>
          <h2 style={{ fontWeight: 700 }}>Something went wrong.</h2>
          <p style={{ color: "#64748B", marginTop: 8 }}>
            The error has been reported. Please refresh and try again.
          </p>
        </div>
      }
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
